import React from 'react'
import { Admin } from '@/types/company'
import { useToast } from '@/components/ui/use-toast'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { userService } from '@/services/userService'
import { useAuth } from '@/context/AuthContext'
import { ADMIN_MENU_PERMISSIONS, ALL_ADMIN_MENU_KEYS } from '@/lib/adminPermissions'

interface EditAdminModalProps {
  admin: Admin
  isOpen: boolean
  onClose: () => void
  onSuccess: () => void
}

export function EditAdminModal({
  admin,
  isOpen,
  onClose,
  onSuccess,
}: EditAdminModalProps) {
  const { toast } = useToast()
  const { user: currentUser } = useAuth()
  const [formData, setFormData] = React.useState({
    name: admin.name,
    email: admin.email,
    password: '',
  })
  const [checkedPermissions, setCheckedPermissions] = React.useState<Set<string>>(
    new Set(admin.permissions && admin.permissions.length > 0 ? admin.permissions : ALL_ADMIN_MENU_KEYS)
  )

  const isEditingSelf = currentUser?._id === admin._id
  const showPermissions = admin.role === 'super_admin'

  const togglePermission = (key: string) => {
    if (isEditingSelf && key === 'users' && checkedPermissions.has('users')) {
      // Kilitlenmeyi önle: kendi hesabından "Kullanıcı Yönetimi" iznini kaldıramazsın
      return
    }
    setCheckedPermissions(prev => {
      const next = new Set(prev)
      if (next.has(key)) {
        next.delete(key)
      } else {
        next.add(key)
      }
      return next
    })
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    try {
      const updateData = {
        name: formData.name,
        email: formData.email,
        ...(formData.password ? { password: formData.password } : {}),
        ...(showPermissions
          ? {
              permissions: checkedPermissions.size === ALL_ADMIN_MENU_KEYS.length
                ? []
                : Array.from(checkedPermissions)
            }
          : {})
      }

      await userService.updateAdmin(admin._id, updateData)
      toast({
        title: 'Başarılı',
        description: 'Yönetici başarıyla güncellendi.',
      })
      onSuccess()
      onClose()
    } catch (error) {
      console.error('Yönetici güncellenirken hata:', error)
      toast({
        variant: 'destructive',
        title: 'Hata',
        description: 'Yönetici güncellenirken bir hata oluştu.',
      })
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Yönetici Düzenle</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name">İsim</Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, name: e.target.value }))
              }
              required
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="email">E-posta</Label>
            <Input
              id="email"
              type="email"
              value={formData.email}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, email: e.target.value }))
              }
              required
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="password">Yeni Şifre (Opsiyonel)</Label>
            <Input
              id="password"
              type="password"
              placeholder="Yeni şifre girin"
              value={formData.password}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, password: e.target.value }))
              }
              minLength={6}
            />
            <p className="text-sm text-muted-foreground">
              Şifre değiştirmek istemiyorsanız boş bırakın
            </p>
          </div>
          {showPermissions && (
            <div className="space-y-2">
              <Label>Panel Erişimi</Label>
              <div className="grid grid-cols-2 gap-x-4 gap-y-2 p-3 rounded-lg border max-h-56 overflow-y-auto">
                {ADMIN_MENU_PERMISSIONS.map(({ key, label }) => (
                  <label key={key} className="flex items-center gap-2 text-sm cursor-pointer">
                    <input
                      type="checkbox"
                      checked={checkedPermissions.has(key)}
                      onChange={() => togglePermission(key)}
                      disabled={isEditingSelf && key === 'users'}
                      className="rounded border-border"
                    />
                    {label}
                  </label>
                ))}
              </div>
              {isEditingSelf && (
                <p className="text-xs text-muted-foreground">
                  Kendi hesabınızdan Kullanıcı Yönetimi yetkisini kaldıramazsınız.
                </p>
              )}
            </div>
          )}
          <div className="flex justify-end space-x-2">
            <Button type="button" variant="outline" onClick={onClose}>
              İptal
            </Button>
            <Button type="submit">Kaydet</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}
